/* iter-training-v-ach-coach — Training Center hub.
 *
 * A modal that renders the 8-lesson training curriculum from
 * `TRAINING_LESSONS` (see `/lib/tours.js`) with per-lesson progress
 * checkmarks. Each row is a one-click "Run lesson" that spotlights the
 * real UI anchors — writers *do* each step, they don't just read.
 *
 * Also (iter-training-v-ach-coach):
 *   • Auto-launches once per user (backfill + first-run) via a
 *     `pw_training_center_seen_v1` localStorage flag.
 *   • Fetches admin-configured lesson videos and shows a floating
 *     player overlay when a lesson has one.
 *
 * Opened from:
 *   • MenuBar → Help → Training Center
 *   • Landing page's "New here?" CTA (future)
 *   • Programmatically via `openTrainingCenter()`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, Circle, Play, RotateCcw, Clock, Sparkles,
  GraduationCap, PlayCircle, Settings,
} from 'lucide-react';
import {
  TRAINING_LESSONS, getTrainingProgress, runTrainingLesson, resetTraining,
} from '@/lib/tours';
import { getPromptMode } from '@/components/OnboardingPromptModeDialog';
import { VideoOverlay } from '@/components/VideoOverlay';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const OPEN_EVENT = 'training-center-open';
const SEEN_KEY = 'pw_training_center_seen_v1';

export function openTrainingCenter() {
  try { window.dispatchEvent(new CustomEvent(OPEN_EVENT)); } catch { /* ignore */ }
}

export default function TrainingCenter() {
  const [open, setOpen]         = useState(false);
  const [progress, setProgress] = useState(getTrainingProgress);
  const [videos, setVideos]     = useState({});
  const [playing, setPlaying]   = useState(null);   // { lessonId, title, url }
  // iter-p2-b — Toast preferences: writer can silence the next-lesson
  // celebration stream. Persisted in localStorage so it survives sessions.
  const [nextToastOff, setNextToastOff] = useState(() => {
    try { return window.localStorage.getItem('pw_disable_next_lesson_toast') === '1'; }
    catch { return false; }
  });
  const toggleNextToast = () => {
    setNextToastOff((prev) => {
      const nextVal = !prev;
      try { window.localStorage.setItem('pw_disable_next_lesson_toast', nextVal ? '1' : '0'); }
      catch { /* ignore */ }
      return nextVal;
    });
  };

  // Fetch admin-set video URLs once.
  const loadVideos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/training-videos`);
      setVideos(r.data?.videos || {});
    } catch { /* silent — no videos configured */ }
  }, []);
  useEffect(() => { loadVideos(); }, [loadVideos]);

  useEffect(() => {
    const onOpen = () => { setProgress(getTrainingProgress()); setOpen(true); };
    const onProgress = () => setProgress(getTrainingProgress());
    window.addEventListener(OPEN_EVENT, onOpen);
    window.addEventListener('training-progress-changed', onProgress);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener('training-progress-changed', onProgress);
    };
  }, []);

  // Backfill auto-launch — one time only per browser. Fires once the
  // user is past the OnboardingPromptModeDialog (i.e. has chosen a
  // prompt mode) but has never opened the Training Center.
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(SEEN_KEY);
      const mode = getPromptMode();
      // "off" users get no autopop of anything — respect the mode.
      if (seen || !mode || mode === 'off') return undefined;
      const t = setTimeout(() => {
        try {
          window.localStorage.setItem(SEEN_KEY, String(Date.now()));
          setProgress(getTrainingProgress());
          setOpen(true);
        } catch { /* ignore */ }
      }, 4500);
      return () => clearTimeout(t);
    } catch { /* ignore */ }
    return undefined;
  }, []);

  const done       = progress.filter((l) => l.completed).length;
  const total      = progress.length;
  const pct        = total ? Math.round((done / total) * 100) : 0;
  const nextLesson = progress.find((l) => !l.completed) || progress[progress.length - 1];

  const launch = (id) => {
    setOpen(false);
    // Give the modal a beat to close so the spotlight math sees the
    // real anchor rectangles instead of the modal overlay.
    setTimeout(() => runTrainingLesson(id), 250);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[85vh] max-w-3xl overflow-hidden bg-white p-0"
          data-testid="training-center-dialog"
        >
          <DialogHeader className="border-b bg-gradient-to-r from-oxblood-50 to-amber-50 px-5 py-3.5">
            <DialogTitle className="flex items-center gap-2 text-[16px]"
                         style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <GraduationCap className="h-5 w-5 text-oxblood-700" />
              Training Center — start producing content
            </DialogTitle>
            <p className="text-[11.5px] text-neutral-600">
              An 8-lesson curriculum from blank page to a book on the Bookstore.
              Each lesson runs against the real workspace — you do each step, you
              don't just read about it.
            </p>
          </DialogHeader>

          <div className="max-h-[65vh] overflow-y-auto p-4">
            {/* Progress + jump CTA */}
            <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">
                    Your progress
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-[20px] font-bold font-mono-stat text-oxblood-700"
                          data-testid="training-progress-count">
                      {done}<span className="text-[13px] text-neutral-500"> / {total}</span>
                    </span>
                    <span className="text-[11.5px] text-neutral-600">lessons complete</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {done < total && (
                    <Button
                      size="sm"
                      className="h-8 gap-1 bg-oxblood-700 text-white hover:bg-oxblood-800"
                      onClick={() => launch(nextLesson.id)}
                      data-testid="training-continue-btn"
                    >
                      <Play className="h-3 w-3" />
                      Continue with lesson {nextLesson.order}
                    </Button>
                  )}
                  {done > 0 && (
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 gap-1 text-[10.5px] text-neutral-500 hover:text-rose-600"
                      onClick={() => { resetTraining(); setProgress(getTrainingProgress()); }}
                      data-testid="training-reset-btn"
                    >
                      <RotateCcw className="h-3 w-3" /> Reset progress
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="h-full bg-oxblood-600 transition-all"
                  style={{ width: `${pct}%` }}
                  data-testid="training-progress-bar"
                />
              </div>
              {/* iter-p2-b — Preferences: silence the next-lesson toast. */}
              <div className="mt-2 flex items-center justify-end gap-1.5 text-[10.5px] text-neutral-500">
                <Settings className="h-3 w-3" />
                <label className="flex items-center gap-1 cursor-pointer"
                       data-testid="training-toast-pref-label">
                  <input
                    type="checkbox"
                    checked={nextToastOff}
                    onChange={toggleNextToast}
                    data-testid="training-toast-pref"
                    className="h-3 w-3 accent-oxblood-700"
                  />
                  Silence next-lesson toast on completion
                </label>
              </div>
            </div>

            {/* Lesson list */}
            <ol className="space-y-2" data-testid="training-lesson-list">
              {progress.map((lesson) => {
                const video = videos[lesson.id];
                return (
                  <li key={lesson.id}
                      className={
                        'flex items-start gap-3 rounded-md border p-3 transition '
                        + (lesson.completed
                          ? 'border-emerald-300 bg-emerald-50/50'
                          : 'border-neutral-200 bg-white hover:border-oxblood-300')
                      }
                      data-testid={`training-lesson-${lesson.id}`}>
                    <div className="mt-0.5">
                      {lesson.completed
                        ? <CheckCircle2 className="h-5 w-5 text-emerald-600"
                                        data-testid={`training-lesson-${lesson.id}-check`} />
                        : <Circle className="h-5 w-5 text-neutral-300" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="text-[13px] font-semibold text-neutral-900">
                          <span className="mr-1.5 rounded-sm bg-oxblood-100 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-oxblood-700">
                            L{lesson.order}
                          </span>
                          {lesson.title}
                        </div>
                        <div className="flex shrink-0 items-center gap-1 text-[10.5px] text-neutral-500">
                          <Clock className="h-3 w-3" /> {lesson.minutes} min
                        </div>
                      </div>
                      <p className="mt-0.5 text-[11.5px] leading-snug text-neutral-600">
                        {lesson.goal}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <Button
                        size="sm"
                        variant={lesson.completed ? 'ghost' : 'outline'}
                        className={
                          'h-7 gap-1 text-[10.5px] '
                          + (lesson.completed ? 'text-emerald-700' : 'text-oxblood-700 hover:bg-oxblood-50')
                        }
                        onClick={() => launch(lesson.id)}
                        data-testid={`training-lesson-${lesson.id}-run`}
                      >
                        <Play className="h-3 w-3" />
                        {lesson.completed ? 'Replay' : 'Run'}
                      </Button>
                      {video && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 gap-1 text-[10px] text-neutral-500 hover:text-oxblood-700"
                          onClick={() => setPlaying({ lessonId: lesson.id, title: lesson.title, url: video })}
                          data-testid={`training-lesson-${lesson.id}-watch`}
                          title="Watch a 30-second explainer"
                        >
                          <PlayCircle className="h-3 w-3" />
                          Watch
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* Certificate */}
            {done === total && (
              <div
                className="mt-4 rounded-md border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-amber-50 p-4 text-center"
                data-testid="training-certificate"
              >
                <Sparkles className="mx-auto h-6 w-6 text-emerald-700" />
                <div className="mt-1 text-[15px] font-semibold text-neutral-900"
                     style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  You're production-ready.
                </div>
                <p className="mt-0.5 text-[11.5px] text-neutral-600">
                  Every lesson complete. Go publish a book — and if you're on Premier,
                  queue three at once with the Bulk Wizard.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating video player overlay — shared with HelpTip + Feature
          Videos admin panel via `VideoOverlay` (iter-p2-d). */}
      {playing && (
        <VideoOverlay
          title={playing.title}
          url={playing.url}
          onClose={() => setPlaying(null)}
          testIdPrefix="training-video-overlay"
        />
      )}
    </>
  );
}
